import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  CreateCompanyRequest,
  CreateCompanyResponse,
  CreateDocumentTypeRequest,
  CreateDocumentTypeResponse,
  DocumentUploadRequest,
  DocumentUpdateRequest,
  FetchCompaniesResponse,
  FetchCompanyDocumentsResponse,
  FetchCompanyResponse,
  FetchDocumentTypeResponse,
  FetchCompanyStatusesResponse,
  UpdateCompanyRequest,
  UpdateCompanyResponse,
  ApproveCompanyResponse,
  RestoreCompanyResponse,
  DocumentOperationResponse,
  UploadDocumentResponse,
  GetAttachmentResponse,
  ResendActivationLinkResponse,
  FetchCompanyWithdrawRequestsResponse,
  FetchCompanyWithdrawRequestResponse,
  CreateCompanyWithdrawRequestRequest,
  CreateCompanyWithdrawRequestResponse,
  UpdateCompanyWithdrawRequestRequest,
  UpdateCompanyWithdrawRequestResponse,
  ApproveCompanyWithdrawRequestResponse,
  RejectCompanyWithdrawRequestResponse,
} from '../../../../types/b2tRatibiPlus';
import { PaginatedRequest } from '../../../../types/api';
import { b2bThriftPlan } from '../../../../api/b2bThriftPlan';

export const fetchCompanies = createAsyncThunk<FetchCompaniesResponse, PaginatedRequest>(
  'company/fetch-all',
  async (params) => {
    const response = await b2bThriftPlan.fetchCompanyies({ includeDeleted: 'true', ...params });
    return response;
  },
);

export const fetchCompany = createAsyncThunk<FetchCompanyResponse, { companyId: number }>(
  'company/get',
  async ({ companyId }) => {
    const response = await b2bThriftPlan.fetchCompany({ companyId });
    return response;
  },
);

export const createCompany = createAsyncThunk<CreateCompanyResponse, CreateCompanyRequest>(
  'company/create',
  async (payload) => {
    const response = await b2bThriftPlan.createCompany(payload);
    return response;
  },
);

export const updateCompany = createAsyncThunk<UpdateCompanyResponse, UpdateCompanyRequest>(
  'company/update',
  async (payload) => {
    const response = await b2bThriftPlan.updateCompany(payload);
    return response;
  },
);

/** Deletes a company by id */
export const deleteCompany = createAsyncThunk<FetchCompanyResponse, number>('company/delete', async (id) => {
  const response = await b2bThriftPlan.deleteCompany(id);
  return response;
});

export const resendActivationLink = createAsyncThunk<ResendActivationLinkResponse, { companyId: number }>(
  'company/resend-activation-link',
  async ({ companyId }) => {
    const response = await b2bThriftPlan.resendActivetionLink(companyId);
    return response;
  },
);

/** Fetches document type by name to get the ID */
export const fetchDocumentType = createAsyncThunk<FetchDocumentTypeResponse>(
  'company/fetch-document-type',
  async () => {
    const response = await b2bThriftPlan.fetchDocumentType();
    return response;
  },
);

export const createDocumentType = createAsyncThunk<CreateDocumentTypeResponse, CreateDocumentTypeRequest>(
  'company/create-document-type',
  async (payload) => {
    const response = await b2bThriftPlan.createDocumentType(payload);
    return response;
  },
);

/** Fetches company statuses */
export const fetchCompanyStatuses = createAsyncThunk<FetchCompanyStatusesResponse>(
  'company/fetch-statuses',
  async () => {
    const response = await b2bThriftPlan.fetchCompanyStatuses();
    return response;
  },
);

/** Approves a company */
export const approveCompany = createAsyncThunk<ApproveCompanyResponse, number>('company/approve', async (id) => {
  const response = await b2bThriftPlan.approveCompany(id);
  return response;
});

/** Restores a company */
export const restoreCompany = createAsyncThunk<RestoreCompanyResponse, number>('company/restore', async (id) => {
  const response = await b2bThriftPlan.restoreCompany(id);
  return response;
});

/** Uploads company document after company creation */
export const uploadCompanyDocument = createAsyncThunk<UploadDocumentResponse, DocumentUploadRequest>(
  'company/upload-document',
  async (payload) => {
    const response = await b2bThriftPlan.uploadCompanyDocument(payload);
    return response;
  },
);

export const fetchCompanyDocuments = createAsyncThunk<FetchCompanyDocumentsResponse, number>(
  'company/fetch-documents',
  async (companyId) => {
    const response = await b2bThriftPlan.fetchCompanyDocuments(companyId);
    return response;
  },
);

/** Updates company document by ID */
export const updateCompanyDocument = createAsyncThunk<DocumentOperationResponse, DocumentUpdateRequest>(
  'company/update-document',
  async (payload) => {
    const response = await b2bThriftPlan.updateCompanyDocument(payload);
    return response;
  },
);

export const deleteCompanyDocument = createAsyncThunk<DocumentOperationResponse, number>(
  'company/delete-document',
  async (id) => {
    const response = await b2bThriftPlan.deleteCompanyDocument(id);
    return response;
  },
);

export const getAttachment = createAsyncThunk<GetAttachmentResponse, number>(
  'company/get-attachment',
  async (payload) => {
    const response = await b2bThriftPlan.getAttachment(payload);
    return response;
  },
);

export const fetchCompanyWithdrawRequests = createAsyncThunk<
  FetchCompanyWithdrawRequestsResponse,
  { companyId: number } & PaginatedRequest
>('company/fetch-withdraw-requests', ({ companyId, ...data }) =>
  b2bThriftPlan.fetchCompanyWithdrawRequests({ companyId, ...data }),
);

export const approveCompanyWithdrawRequest = createAsyncThunk<
  ApproveCompanyWithdrawRequestResponse,
  { companyId: number; id: number }
>('company/approve-withdraw-request', ({ companyId, id }) =>
  b2bThriftPlan.approveCompanyWithdrawRequest(companyId, id),
);

export const rejectCompanyWithdrawRequest = createAsyncThunk<
  RejectCompanyWithdrawRequestResponse,
  { companyId: number; id: number }
>('company/reject-withdraw-request', ({ companyId, id }) => b2bThriftPlan.rejectCompanyWithdrawRequest(companyId, id));

export const fetchCompanyWithdrawRequest = createAsyncThunk<
  FetchCompanyWithdrawRequestResponse,
  { companyId: number; id: number }
>('company/fetch-withdraw-request', ({ companyId, id }) => b2bThriftPlan.fetchCompanyWithdrawRequest(companyId, id));

export const createCompanyWithdrawRequest = createAsyncThunk<
  CreateCompanyWithdrawRequestResponse,
  { companyId: number } & CreateCompanyWithdrawRequestRequest
>('company/create-withdraw-request', ({ companyId, ...payload }) =>
  b2bThriftPlan.createCompanyWithdrawRequest(companyId, payload),
);

export const updateCompanyWithdrawRequest = createAsyncThunk<
  UpdateCompanyWithdrawRequestResponse,
  { companyId: number; id: number } & UpdateCompanyWithdrawRequestRequest
>('company/update-withdraw-request', ({ companyId, id, ...payload }) =>
  b2bThriftPlan.updateCompanyWithdrawRequest(companyId, id, payload),
);
